// eslint-disable-next-line import/no-cycle
import { DataDog } from '../datadog'
import {
  NORDIC_CURRENCIES,
  isSEKCurrency,
  isEURCurrency,
  isFinlandCulture,
} from '../constants/currencies'

/**
 * Redirect to another page
 * @param {string} nextUrl
 */
export const redirect = nextUrl => {
  const mfeElement = window.tuiPaymentsMFEInstance

  if (mfeElement?.onRedirecting) {
    DataDog.info(`onRedirecting hook has been enabled`)

    mfeElement
      .onRedirecting?.({ url: nextUrl })
      .then(() => {
        DataDog.info(`onRedirecting Promise resolved`)
        DataDog.info(`Redirecting to ${nextUrl}`)
        window.location.href = nextUrl
      })
      .catch(() => {
        DataDog.info(`onRedirecting Promise rejected`)
        console.log('onRedirecting Promise rejected')
      })
  } else {
    DataDog.info(`Redirecting to ${nextUrl}`)
    window.location.href = nextUrl
  }
}

/**
 * Check that object is empty
 * @param {object} obj
 * @returns {boolean}
 */
export const isEmpty = obj => !Object.keys(obj).length

/**
 * Check if there is mobile browser
 * @returns {boolean}
 */
export const isMobile = () => {
  let hasChecked = false
  // eslint-disable-next-line  no-useless-escape
  ;(function checkMobile(a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a,
      ) ||
      // eslint-disable-next-line
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    ) {
      hasChecked = true
    }
  })(navigator.userAgent || navigator.vendor || window.opera)

  return (
    navigator.userAgentData?.mobile ||
    hasChecked ||
    (window.innerWidth <= 800 && window.innerHeight <= 600)
  )
}

/**
 * To fill the placeholders in translations, especially pluralisations
 * @param format
 * @param args
 * @returns {*}
 */
export const sprintf = (format, ...args) => {
  let i = 0
  return format.replace(/%s/g, () => args[i++])
}

/**
 * Get Number with ordinal, like 1 -> 1st, 2 -> 2nd, etc.
 * @param n
 * @returns {string}
 */
export const getNumberWithOrdinal = n => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

/**
 * Creates array from range of numbers, like
 * (1, 5) => [1, 2, 3, 4, 5]
 * @param start
 * @param stop
 * @returns {*[]}
 */
export const makeArrayFromRangeOfNumbers = (start, stop) => {
  const a = [start]
  let b = start
  while (b < stop) {
    a.push((b += 1))
  }
  return a
}

export const getSymbol = (currency, culture) => {
  if (isEURCurrency(currency) && isFinlandCulture(culture)) {
    return 'e'
  }
  const symbols = {
    SEK: ':-',
    DKK: ',-',
    NOK: ',-',
  }
  return symbols[currency] || ''
}

/**
 * Convert number to currency format
 * @param currency
 * @param locale
 * @returns {function(*): string}
 */
export const formatAsCurrency = (currency, locale) => value => {
  const options = {
    currency,
    minimumFractionDigits: 2,
  }
  const data = (value / 100).toLocaleString(locale, options)
  if (isEURCurrency(currency) && isFinlandCulture(locale)) {
    return `${data} ${getSymbol(currency, locale)}`
  }
  if (NORDIC_CURRENCIES.includes(currency)) {
    return `${data} ${getSymbol(currency)}`
  }
  if (isSEKCurrency(currency)) {
    return `SEK ${data} ${getSymbol(currency)}`
  }
  return (value / 100).toLocaleString(locale, { ...options, style: 'currency' })
}

/**
 * Check if number exists between two other numbers
 * @param numberFrom
 * @param numberTo
 * @param numberCheck
 * @returns {boolean}
 */
export const isNumberBetweenTwoNumbers = (numberFrom, numberTo, numberCheck) =>
  numberCheck <= numberTo && numberCheck >= numberFrom

/**
 * Format Date to look like this: 7th September 2022
 * @param date,
 * @param l10n,
 * @returns {function(*): string}
 */

export const formatDateWithOrdinal = (date, l10n) => plusMonth => {
  const nextDate = new Date(
    new Date(date).setMonth(new Date(date).getMonth() + plusMonth),
  )
  const formattedDay = getNumberWithOrdinal(nextDate.getDate())

  const formattedMonth = nextDate.toLocaleString('default', {
    month: 'long',
  })
  const formattedYear = nextDate.toLocaleString('default', {
    year: 'numeric',
  })
  return `${formattedDay} ${l10n.t(`${formattedMonth}`)} ${formattedYear}`
}

/**
 * Get text from selection
 * @returns {string}
 */
export const getSelectionText = () => {
  let text = ''
  if (window.getSelection) {
    text = window.getSelection().toString()
  } else if (document.selection && document.selection.type !== 'Control') {
    text = document.selection.createRange().text
  }
  return text
}

/**
 * Check for existing <script> tags with this src
 * @param src
 * @returns {boolean | Node}
 */
const getExisting = src => {
  if (!src) return false
  return document.querySelector(`script[src="${src}"]`)
}

/**
 *
 * @param {string} url the script url to load
 * @param {function} onLoad callback when script loaded successfully
 * @param {function} onError callback when script loading was failed
 * @param {boolean} isLoadInMFE if this needs to be loaded inside MFE
 * @returns {Node} the dom object for scritp
 */
export const loadScript = (url, onLoad, onError, isLoadInMFE) => {
  const existingScript = getExisting(url)

  if (existingScript) {
    onLoad()
    return existingScript
  }

  const script = document.createElement('script')
  script.addEventListener('load', onLoad)
  script.addEventListener('error', onError)
  script.src = url

  if (isLoadInMFE) {
    window.tuiPaymentsMFEInstance.shadow.appendChild(script)
  } else {
    document.body.appendChild(script)
  }

  return script
}

/**
 *
 * @param {String} url
 * @returns {Promise}
 */
export const loadScriptAsync = url =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    let r = false
    const onLoad = () => {
      if (!r && (!script.readyState || script.readyState === 'complete')) {
        r = true
        resolve()
      }
    }

    script.onload = onLoad
    script.onreadystatechange = onLoad
    script.onerror = err => reject(err)

    document.body.appendChild(script)
  })

/**
 *
 * @param {Node} script
 * @param {function} onLoad
 * @param {funtion} onError
 */
export const unloadScript = (script, onLoad, onError) => {
  if (!script) return
  document.body.removeChild(script)
  script.removeEventListener('load', onLoad)
  script.removeEventListener('error', onError)
}

/**
 * Get uniq elements of objects array mapped by id
 * @param {[Object]} arr
 * @returns Array
 */
export const getUniqArray = arr =>
  [...new Set(arr.map(({ id }) => id))].map(e => arr.find(({ id }) => id === e))

/**
 * Get absolute url to relatiove one
 * @param {String} url
 * @returns
 */
export const getAbsoluteUrl = url => {
  const a = document.createElement('a')
  a.href = url
  return a.href
}

/**
 * Get card number splitted by 4 digits
 * @param {string} cardNumber
 * @returns
 */
export const getFormattedCardNumber = cardNumber => {
  const nextNumber = cardNumber.replace(/\s/g, '')
  if (nextNumber.length > 0) {
    const acc = []

    for (let i = 0; i < nextNumber.length; i += 4) {
      acc.push(nextNumber.slice(i, i + 4))
    }

    return acc.join(' ')
  }

  return cardNumber
}

/**
 * Checks that symbol is allowed for input with type='number'
 * @param event
 * @returns {boolean}
 */
export const isAllowedNumberInputKey = event => {
  const key = event.charCode || event.keyCode || 0

  const isNavigationKey = [8, 9, 13, 46, 35, 36, 37, 39].includes(key)
  const isNumberKey = key >= 48 && key <= 57
  const isNumpadKey = key >= 96 && key <= 105

  return isNavigationKey || isNumberKey || isNumpadKey
}

/**
 * Masking all card numbers as 'X' except first 4 and last 6 numbers
 * @param creditCard
 * @returns {`${string}${string}${string}`}
 */
export const maskifyCard = creditCard => {
  const last4Characters = creditCard.substr(-4)
  const first6Character = creditCard.substr(0, 6)
  const maskingCharacters = creditCard
    .substr(6, creditCard.length - 10)
    .replace(/\d/g, 'X')
  return `${first6Character}${maskingCharacters}${last4Characters}`
}

/**
 * Polyfill for String.prototype.replaceAll, since Safari 12.1 doesn't support it
 * @param text
 * @param search
 * @param replacement
 * @returns {*}
 */
export const replaceAll = (text, search, replacement) =>
  text?.split(search).join(replacement)

/**
 * Capitalise phrase
 * @param phrase
 * @returns {string}
 */
export const toCapitalise = phrase =>
  phrase
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

/**
 * Get support phone number bt country
 * @param culture
 * @returns {string}
 */
export const getSupportPhoneNumber = culture => {
  switch (culture) {
    case 'fr-FR':
    case 'fr-MA':
    case 'fr-BE':
      return '(0) 2 717 86 61'
    case 'nl-NL':
    case 'nl-BE':
      return '(0)88 – 088 58 85'
    default:
      return ''
  }
}

/**
 * Debounce function
 * @param func
 * @param delay
 * @returns {(function(...[*]): void)|*}
 */
export const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * Adds query params into URL string
 * @param url
 * @param queryParams
 * @returns {*|string}
 */
export const addQueryParams = (url, queryParams) => {
  if (!queryParams || typeof queryParams !== 'object') return url

  const [basePath, existingQuery] = url.split('?')
  const searchParams = new URLSearchParams(existingQuery || '')

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, value)
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}

export const DEFAULT_APPLE_PAY_LOCALE = 'en-GB'

/**
 * Locales accepted by Apple Pay's `ApplePayButtonLocale`.
 *
 * This is Apple's own allowlist, which is NOT the same as the payment-UI
 * cultures in `LOCALES`. OPP cultures such as `nl-BE`, `fr-BE`, `fr-MA`,
 * `es-VE`, `nn-NO` and `sv-FI` are not accepted by Apple Pay and must fall
 * back to a supported locale. Keys are lowercased for case-insensitive
 * lookup; values are the exact casing Apple expects.
 * @see https://developer.apple.com/documentation/applepayontheweb/applepaybuttonlocale
 */
export const APPLE_PAY_LOCALES = {
  'ar-ab': 'ar-AB',
  'ca-es': 'ca-ES',
  'cs-cz': 'cs-CZ',
  'da-dk': 'da-DK',
  'de-de': 'de-DE',
  'el-gr': 'el-GR',
  'en-au': 'en-AU',
  'en-gb': 'en-GB',
  'en-us': 'en-US',
  'es-es': 'es-ES',
  'es-mx': 'es-MX',
  'fi-fi': 'fi-FI',
  'fr-ca': 'fr-CA',
  'fr-fr': 'fr-FR',
  'he-il': 'he-IL',
  'hi-in': 'hi-IN',
  'hr-hr': 'hr-HR',
  'hu-hu': 'hu-HU',
  'id-id': 'id-ID',
  'it-it': 'it-IT',
  'ja-jp': 'ja-JP',
  'ko-kr': 'ko-KR',
  'ms-my': 'ms-MY',
  'nb-no': 'nb-NO',
  'nl-nl': 'nl-NL',
  'pl-pl': 'pl-PL',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-PT',
  'ro-ro': 'ro-RO',
  'ru-ru': 'ru-RU',
  'sk-sk': 'sk-SK',
  'sv-se': 'sv-SE',
  'th-th': 'th-TH',
  'tr-tr': 'tr-TR',
  'uk-ua': 'uk-UA',
  'vi-vn': 'vi-VN',
  'zh-cn': 'zh-CN',
  'zh-hk': 'zh-HK',
  'zh-tw': 'zh-TW',
}

/**
 * Normalise a BE-provided culture into a locale accepted by Apple Pay.
 *
 * Apple Pay's `ApplePayButtonLocale` expects BCP-47 tags with a lowercase
 * language subtag and an uppercase region subtag (e.g. `en-GB`, `sv-SE`).
 * The BE may send the culture with an underscore separator (e.g. `en_GB`)
 * and/or inconsistent casing. This converts it, then validates it against
 * Apple Pay's own allowlist (not the wider `LOCALES` payment-UI set),
 * falling back to `en-GB` when the culture is missing or unsupported by
 * Apple Pay (e.g. `nl-BE`, `fr-MA`, `nn-NO`).
 *
 * @param {string} culture BE-provided culture, e.g. `sv_SE`, `en-GB`.
 * @returns {string} An Apple Pay compatible locale, defaulting to `en-GB`.
 */
export const toApplePayLocale = culture => {
  if (!culture || typeof culture !== 'string') {
    return DEFAULT_APPLE_PAY_LOCALE
  }

  const normalised = replaceAll(culture.trim(), '_', '-').toLowerCase()

  return APPLE_PAY_LOCALES[normalised] || DEFAULT_APPLE_PAY_LOCALE
}
